import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Users, ArrowRight, Star, ChevronRight } from 'lucide-react';
import NewsletterForm from '../components/NewsletterForm';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [groupedEvents, setGroupedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroupedEvents();
  }, []);

  const fetchGroupedEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/grouped`);
      setGroupedEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedTitle = (event) => {
    const lang = i18n.language;
    return event[`title_${lang}`] || event.title_it;
  };

  const getLocalizedDescription = (event) => {
    const lang = i18n.language;
    const desc = event[`description_${lang}`] || event.description_it;
    return desc.length > 120 ? desc.substring(0, 120) + '...' : desc;
  };

  // Priority order for displaying events on homepage (as per user request)
  const priorityOrder = [
    'Tunisia - Maestri della Sabbia (M.D.S.)',  // Maestri di sabbia
    'Tunisia - 3 Deserti',                       // 3 Deserti
    'Desert Slim',                               // Desert Slim
    'Libia - Akakus Murzuk Experience',          // Libia
    'Enduring del Cilento',                      // Enduring del Cilento
    'Enduring dei Vulcani',                      // Enduring dei Vulcani
    'Italian Cannonball',                        // Italian Cannonball
    'Hellas Roadbook School',                    // Hellas Roadbook School
    'Colfiorito Adventure',                      // Colfiorito
    'Trinacria Enduring',                        // Trinacria
    'Viking Offroad',                            // Viking Offroad (Iceland)
    'Hellas Offroad Tour',                       // Hellas Offroad Tour
    'Colombia Adventure - Chicamocha',           // Colombia Chicamocha
    'Colombia Adventure - Bogotà Medellin',      // Colombia Bogotà-Medellin
    'Colombia Adventure - Macarena',             // Colombia Macarena
    'Offroad University - Corso di Guida'        // Offroad University
  ];
  
  // Events that should NOT show "da" prefix (fixed price)
  const fixedPriceEvents = [];

  // Filter and sort events by priority
  const getOrderedEvents = () => {
    const ordered = [];
    
    // First add events in priority order
    priorityOrder.forEach(title => {
      const event = groupedEvents.find(e => e.title_it === title);
      if (event) {
        ordered.push(event);
      }
    });
    
    // Then add remaining events not in priority list
    groupedEvents.forEach(event => {
      if (!priorityOrder.includes(event.title_it) && !ordered.find(e => e.title_it === event.title_it)) {
        ordered.push(event);
      }
    });
    
    return ordered;
  };

  const orderedEvents = getOrderedEvents();

  return (
    <div className="min-h-screen">
      <SEOHead />
      {/* Hero Section */}
      <div className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/hero-homepage.jpg')" }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]" style={{textShadow: '2px 2px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)'}}>
              {t('hero.title')}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 text-white font-medium drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" style={{textShadow: '1px 1px 6px rgba(0,0,0,0.9), 0 0 15px rgba(0,0,0,0.6)'}}>
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/events"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors shadow-lg"
                data-testid="discover-events-btn"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-2" size={20} />
              </Link>
              
              <a
                href="https://wa.me/393793793030?text=Ciao%2C%20vorrei%20informazioni%20sugli%20eventi%20Offroad%20Italia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-green-500 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-green-600 transition-colors shadow-lg"
                data-testid="whatsapp-btn"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Events - Grouped by Tour Type */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('events.upcoming')}</h2>
          <p className="text-xl text-gray-600">{i18n.language === 'it' ? 'Scegli la tua prossima avventura' : i18n.language === 'en' ? 'Choose your next adventure' : i18n.language === 'de' ? 'Wähle dein nächstes Abenteuer' : i18n.language === 'fr' ? 'Choisissez votre prochaine aventure' : 'Elige tu próxima aventura'}</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {orderedEvents.map((event) => (
              <div 
                key={event.id} 
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`event-card-${event.id}`}
              >
                <div 
                  className="h-48 bg-cover bg-top relative" 
                  style={{ backgroundImage: `url(${event.image_url})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase">
                      {event.event_type}
                    </span>
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{getLocalizedTitle(event)}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{getLocalizedDescription(event)}</p>
                  
                  {/* All Available Dates */}
                  {event.all_dates && event.all_dates.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center text-gray-700 mb-2">
                        <Calendar size={16} className="mr-2 text-blue-600" />
                        <span className="text-sm font-semibold">
                          {event.all_dates.length > 1 ? `${event.all_dates.length} ${t('events.dates')}:` : `${i18n.language === 'it' ? 'Data' : i18n.language === 'en' ? 'Date' : i18n.language === 'de' ? 'Datum' : i18n.language === 'fr' ? 'Date' : 'Fecha'}:`}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {event.all_dates.map((date, idx) => (
                          <Link 
                            key={date.id}
                            to={`/events/${date.id}`}
                            className="block bg-blue-50 hover:bg-blue-100 p-2 rounded-lg border border-blue-200 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-blue-900 font-medium text-sm">
                                {date.date_start} → {date.date_end}
                              </span>
                              {date.spots_available !== null && date.spots_available <= 3 && date.spots_available > 0 && (
                                <span className="bg-orange-500 text-white px-2 py-0.5 rounded-full text-xs font-bold animate-pulse">
                                  {i18n.language === 'it' ? 'ULTIMI' : i18n.language === 'en' ? 'LAST' : i18n.language === 'de' ? 'LETZTE' : i18n.language === 'fr' ? 'DERNIERS' : 'ÚLTIMOS'} {date.spots_available}!
                                </span>
                              )}
                              {date.spots_available !== null && date.spots_available > 3 && (
                                <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                                  {date.spots_available} {t('events.spots').toLowerCase().split(' ')[0]}
                                </span>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin size={16} className="mr-2" />
                    <span className="text-sm">{event.location}</span>
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      {(event.price_own || event.price_rent) && (
                        <div className="text-xl font-bold text-blue-600">
                          {fixedPriceEvents.includes(event.title_it) ? '' : 'da '}€{event.price_own || event.price_rent}
                        </div>
                      )}
                    </div>
                    <Link
                      to={`/events/${event.all_dates?.[0]?.id || event.id}`}
                      className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                      data-testid={`view-details-btn-${event.id}`}
                    >
                      {t('events.details')}
                      <ChevronRight size={18} className="ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link
            to="/events"
            className="inline-flex items-center text-blue-600 font-semibold text-lg hover:text-blue-700 transition-colors"
            data-testid="view-all-events-btn"
          >
            {t('events.viewAll')}
            <ArrowRight className="ml-2" size={24} />
          </Link>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('reviews.title')}</h2>
            <p className="text-xl text-gray-600">{t('reviews.subtitle')}</p>
            
            <div className="flex items-center justify-center mt-6 mb-4">
              <img 
                src="/images/google-5-star-badge.png" 
                alt="Google 5 Star Rating" 
                className="h-32 w-auto"
              />
            </div>
            
            <div className="flex items-center justify-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={32} className="text-yellow-400 fill-current" />
              ))}
              <span className="ml-3 text-2xl font-bold text-gray-900">200+ {i18n.language === 'it' ? 'Recensioni' : i18n.language === 'en' ? 'Reviews' : i18n.language === 'de' ? 'Bewertungen' : i18n.language === 'fr' ? 'Avis' : 'Reseñas'}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} className="text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                {i18n.language === 'it' ? 'Esperienza incredibile! Organizzazione perfetta, paesaggi mozzafiato e un gruppo fantastico.' : i18n.language === 'en' ? 'Incredible experience! Perfect organization, breathtaking landscapes, and a fantastic group.' : i18n.language === 'de' ? 'Unglaubliche Erfahrung! Perfekte Organisation, atemberaubende Landschaften und eine fantastische Gruppe.' : i18n.language === 'fr' ? "Expérience incroyable! Organisation parfaite, paysages à couper le souffle et un groupe fantastique." : '¡Experiencia increíble! Organización perfecta, paisajes impresionantes y un grupo fantástico.'}
              </p>
              <p className="text-gray-600 font-semibold text-sm">- Marco R.</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} className="text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                {i18n.language === 'it' ? 'Ugo e il suo team sono professionali e appassionati. Il tour in Tunisia è stato fantastico!' : i18n.language === 'en' ? 'Ugo and his team are professional and passionate. The Tunisia tour was fantastic!' : i18n.language === 'de' ? 'Ugo und sein Team sind professionell und leidenschaftlich. Die Tunesien-Tour war fantastisch!' : i18n.language === 'fr' ? "Ugo et son équipe sont professionnels et passionnés. Le tour en Tunisie était fantastique!" : '¡Ugo y su equipo son profesionales y apasionados. ¡El tour en Túnez fue fantástico!'}
              </p>
              <p className="text-gray-600 font-semibold text-sm">- Luca M.</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} className="text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                {i18n.language === 'it' ? 'Sicurezza, divertimento e avventura. Consiglio Offroad Italia a tutti gli appassionati!' : i18n.language === 'en' ? 'Safety, fun, and adventure. I recommend Offroad Italia to all enthusiasts!' : i18n.language === 'de' ? 'Sicherheit, Spaß und Abenteuer. Ich empfehle Offroad Italia allen Enthusiasten!' : i18n.language === 'fr' ? "Sécurité, amusement et aventure. Je recommande Offroad Italia à tous les passionnés!" : '¡Seguridad, diversión y aventura. ¡Recomiendo Offroad Italia a todos los entusiastas!'}
              </p>
              <p className="text-gray-600 font-semibold text-sm">- Andrea S.</p>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://www.google.com/search?q=offroad+italia+recensioni"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg shadow-lg"
            >
              {t('reviews.readMore')}
              <ArrowRight className="ml-2" size={24} />
            </a>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <NewsletterForm />
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{i18n.language === 'it' ? 'Pronto per l\'Avventura?' : i18n.language === 'en' ? 'Ready for Adventure?' : i18n.language === 'de' ? 'Bereit für das Abenteuer?' : i18n.language === 'fr' ? 'Prêt pour l\'Aventure?' : '¿Listo para la Aventura?'}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {i18n.language === 'it' ? 'Unisciti a noi nei nostri tour offroad e vivi emozioni indimenticabili!' : i18n.language === 'en' ? 'Join us on our offroad tours and experience unforgettable emotions!' : i18n.language === 'de' ? 'Begleite uns auf unseren Offroad-Touren und erlebe unvergessliche Emotionen!' : i18n.language === 'fr' ? 'Rejoignez-nous dans nos tours offroad et vivez des émotions inoubliables!' : '¡Únete a nosotros en nuestros tours offroad y vive emociones inolvidables!'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              to="/events"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              {t('nav.events')}
            </Link>
            <a
              href="https://wa.me/393793793030?text=Ciao%2C%20vorrei%20informazioni%20sugli%20eventi%20Offroad%20Italia"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-600 transition-colors shadow-lg"
            >
              {t('contact.whatsapp')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
